import { useEffect, useState } from 'react'
import { api } from '@pilot-hub/api-client'

/** Vorkommnisse je Flug (EGRED 2 Flugbuch): Ja/Nein (Standard Nein), leer = nicht angegeben (ältere Flüge),
 *  Bemerkung nur bei Ja. Meldungen der Drohne (DJI HMS) ab Stufe „Achtung“ im Flugzeitraum schlagen ein
 *  Vorkommnis vor (Text zum Übernehmen); sie stehen unabhängig davon auch im Flugbericht. */
export type VorkommnisWert = '' | 'JA' | 'NEIN'

export const vorkommnisAusApi = (v: boolean | null | undefined): VorkommnisWert => (v === true ? 'JA' : v === false ? 'NEIN' : '')
export const vorkommnisZuApi = (v: VorkommnisWert): boolean | null => (v === 'JA' ? true : v === 'NEIN' ? false : null)
/** Bemerkung, die gespeichert wird: nur bei Vorkommnis = Ja */
export const vorkommnisBemerkungZuApi = (v: VorkommnisWert, bemerkung: string): string | null =>
  (v === 'JA' ? bemerkung.trim() || null : null)
export const vorkommnisText = (v: boolean | null | undefined) => (v === true ? 'Ja' : v === false ? 'Nein' : '—')

/** Fehlermeldung oder null; pflicht: Ja/Nein muss gewählt sein (z. B. beim Flugende). */
export function vorkommnisFehler(wert: VorkommnisWert, bemerkung: string, pflicht = false): string | null {
  if (pflicht && !wert) return 'Bitte angeben, ob es Vorkommnisse gab.'
  if (wert === 'JA' && !bemerkung.trim()) return 'Bitte das Vorkommnis in der Bemerkung beschreiben.'
  return null
}

export interface HmsMeldung {
  code: string
  stufe: number | null
  stufe_label: string
  meldung: string | null
  erstmals: string
  behoben_am: string | null
  in_der_luft: boolean | null
}
export interface FlugHms {
  meldungen: HmsMeldung[]
  /** Sachverhalt-Vorschlag aus den Meldungen ab „Achtung“, null wenn es keine gibt */
  vorschlag: string | null
}

/** HMS-Meldungen eines Fluges laden (GET /fluege/{id}/hms); null solange nicht geladen oder ohne Flug. */
export function useFlugHms(flugId: number | null | undefined): FlugHms | null {
  const [hms, setHms] = useState<FlugHms | null>(null)
  useEffect(() => {
    setHms(null)
    if (!flugId) return
    let aktiv = true
    api.get(`/fluege/${flugId}/hms`).then(r => { if (aktiv) setHms(r.data as FlugHms) }).catch(() => {})
    return () => { aktiv = false }
  }, [flugId])
  return hms
}

/** Bemerkung mit dem HMS-Vorschlag ergänzen (nicht doppelt). */
export const mitHmsVorschlag = (bemerkung: string, vorschlag: string) =>
  bemerkung.includes(vorschlag) ? bemerkung : (bemerkung.trim() ? `${bemerkung.trim()}\n\n${vorschlag}` : vorschlag)

const uhrzeit = (s: string) => new Date(s).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

interface VorkommnisFelderProps {
  wert: VorkommnisWert
  bemerkung: string
  onChange: (wert: VorkommnisWert, bemerkung: string) => void
  disabled?: boolean
  /** Meldungen der Drohne im Flug (optional) */
  hms?: FlugHms | null
}

export function VorkommnisFelder({ wert, bemerkung, onChange, disabled = false, hms }: VorkommnisFelderProps) {
  const relevant = (hms?.meldungen ?? []).filter(m => (m.stufe ?? 0) >= 1)
  const uebernommen = !!hms?.vorschlag && wert === 'JA' && bemerkung.includes(hms.vorschlag)
  const knopf = (v: VorkommnisWert, text: string, aktivKlasse: string) => (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={wert === v}
      onClick={() => onChange(v, bemerkung)}
      className={`min-h-[44px] flex-1 rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        wert === v ? aktivKlasse : 'border-dark-border text-dark-text-muted hover:bg-dark-bg-tertiary'
      }`}
    >
      {text}
    </button>
  )
  return (
    <div className="space-y-2">
      <label className="label">Vorkommnisse</label>
      {relevant.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100">
          <p className="font-medium">Die Drohne hat während des Flugs {relevant.length === 1 ? 'eine Meldung' : `${relevant.length} Meldungen`} gesendet – Vorkommnis prüfen</p>
          <ul className="mt-1.5 space-y-1 text-xs text-amber-100/90">
            {relevant.map(m => (
              <li key={`${m.code}-${m.erstmals}`}>
                <span className="font-semibold">{uhrzeit(m.erstmals)} {m.stufe_label}:</span> {m.meldung || 'ohne Klartext'} <span className="text-amber-200/60">({m.code})</span>
              </li>
            ))}
          </ul>
          {!disabled && hms?.vorschlag && (
            <button type="button" disabled={uebernommen} onClick={() => onChange('JA', mitHmsVorschlag(bemerkung, hms.vorschlag!))}
              className="mt-2 min-h-[44px] rounded-lg border border-amber-500/60 px-3 py-2 text-xs font-medium text-amber-100 hover:bg-amber-500/20 disabled:cursor-default disabled:opacity-60">
              {uebernommen ? 'Als Vorkommnis übernommen' : 'Als Vorkommnis übernehmen'}
            </button>
          )}
          <p className="mt-1.5 text-[11px] text-amber-200/70">Die Meldungen stehen auch ohne Vorkommnis im Flugbericht.</p>
        </div>
      )}
      <div className="flex gap-2">
        {knopf('NEIN', 'Nein – keine Vorkommnisse', 'border-green-500/60 bg-green-600/20 text-green-200')}
        {knopf('JA', 'Ja – Vorkommnis', 'border-red-500/60 bg-red-600/20 text-red-200')}
      </div>
      {/* Bemerkung nur bei Vorkommnis; bei "Nein" ausgeblendet und nicht gespeichert */}
      {wert === 'JA' && (
        <textarea
          value={bemerkung}
          onChange={e => onChange(wert, e.target.value)}
          disabled={disabled}
          rows={3}
          className="input min-h-[80px] w-full resize-y"
          placeholder="Was ist passiert? (Pflicht bei Vorkommnis)"
        />
      )}
    </div>
  )
}
